import { useState, useEffect } from 'react';
import { 
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@etherisc/ui-kit';
import { 
  CalendarIcon,
  CheckCircleIcon,
} from 'lucide-react';
import { WizardStepProps } from '../../../framework/wizard';
import type { QuoteWizardData } from '../QuoteWizard';
import { calculateTotalSumInsured, calculateTotalHectaresAnimalCount } from '../utils/quoteDataConverter';
import { createCompleteQuote } from '../utils/quoteHelpers';
import { perils } from '../../../config/products/product';
import { getPerilUnit as getPerilUnitFromRegistry } from '../../../types/perils';

/**
 * Distribute weights equally among the given peril IDs,
 * rounded to 1 decimal place. The last peril absorbs the remainder
 * so the total is exactly 100.
 */
function distributeWeightsEqually(perilIds: string[]): Record<string, number> {
  const weights: Record<string, number> = {};
  const count = perilIds.length;
  if (count === 0) return weights;
  if (count === 1) {
    weights[perilIds[0]] = 100;
    return weights;
  }
  const equalWeight = Math.floor((100 / count) * 10) / 10;
  let remaining = 100;
  perilIds.forEach((id, index) => {
    if (index === count - 1) {
      weights[id] = Math.round(remaining * 10) / 10;
    } else {
      weights[id] = equalWeight;
      remaining -= equalWeight;
    }
  });
  return weights;
}

/**
 * Extract a numeric weight (0-100 %) from a product config weight entry.
 * Accepts plain numbers or { min, max } range objects (midpoint used).
 * Values <= 1 are treated as fractions and multiplied by 100.
 */
function extractWeightFromConfig(config: unknown): number {
  if (typeof config === 'number') {
    return config <= 1 && config > 0 ? config * 100 : config;
  }
  if (typeof config === 'object' && config !== null) {
    const obj = config as Record<string, unknown>;
    if ('value' in obj && typeof obj.value === 'number') {
      const v = obj.value as number;
      return v <= 1 && v > 0 ? v * 100 : v;
    }
    if ('min' in obj && 'max' in obj) {
      const mid = ((obj.min as number) + (obj.max as number)) / 2;
      return mid <= 1 && mid > 0 ? mid * 100 : mid;
    }
  }
  return 0;
}

export function CoverageDetailsStepNew({ 
  data, 
  updateData, 
  errors 
}: WizardStepProps<QuoteWizardData>) {
  
  // Note: Using shared createCompleteQuote utility to avoid code duplication
  
  // Helper functions to extract perils config from quote object (single source of truth)
  const getPerils = () => {
    return data.quote?.perils_config?.perils?.map((p: { peril_id: string }) => p.peril_id) || [];
  };
  
  const getPerilWeights = () => {
    const weights: Record<string, number> = {};
    data.quote?.perils_config?.perils?.forEach((p: { peril_id: string; weight?: number }) => {
      if (p.peril_id && p.weight) {
        weights[p.peril_id] = p.weight;
      }
    });
    return weights;
  };
  
  const [selectedPerils, setSelectedPerils] = useState<string[]>(getPerils());
  const [perilWeights, setPerilWeights] = useState<Record<string, number>>(getPerilWeights());
  const [pentadValidation, setPentadValidation] = useState<{ 
    isValid: boolean; 
    message?: string;
    startDateValid?: boolean;
    endDateValid?: boolean;
    startDateMessage?: string;
    endDateMessage?: string;
  }>({ isValid: true });
  // const [coveragePeriodLimits, setCoveragePeriodLimits] = useState<{
  //   type: 'duration_days' | 'date_range';
  //   minDurationDays?: number;
  //   maxDurationDays?: number;
  //   startDateRange?: { earliest: string; latest: string };
  //   endDateRange?: { earliest: string; latest: string };
  // } | null>(null);
  const [availablePerils, setAvailablePerils] = useState<Array<{ id: string; name: string; defaultWeight: number; unit: string }>>([]);
  const [weightingEnforcement, setWeightingEnforcement] = useState<'default' | 'fixed' | 'range'>('default');
  const [weightingRanges, setWeightingRanges] = useState<Record<string, { min: number; max: number }>>({});
  const [coveragePeriodLimits, setCoveragePeriodLimits] = useState<{
    type: 'duration_days' | 'date_range';
    dateValidation?: 'none' | 'pentads';
    minDurationDays?: number;
    maxDurationDays?: number;
    startDateRange?: { earliest: string; latest: string };
    endDateRange?: { earliest: string; latest: string };
  } | null>(null);

  // Combine framework validation errors with pentad validation errors
  const allErrors = [...errors];
  
  // Add pentad validation errors
  if (data.quote?.coverageStartDate && pentadValidation.startDateValid === false && pentadValidation.startDateMessage) {
    allErrors.push(`Start Date: ${pentadValidation.startDateMessage}`);
  }
  if (data.quote?.coverageEndDate && pentadValidation.endDateValid === false && pentadValidation.endDateMessage) {
    allErrors.push(`End Date: ${pentadValidation.endDateMessage}`);
  }

  // Get specific errors for individual field display
  const startDateError = errors.find(e => e.includes('start date'));
  const endDateError = errors.find(e => e.includes('end date'));

  // Get current date for constraints
  const today = new Date();
  const nextYear = new Date();
  nextYear.setFullYear(nextYear.getFullYear() + 1);

  // Run pentad validation on first render and when dates change
  useEffect(() => {
    if (data.quote?.coverageStartDate) {
      const startDateStr = typeof data.quote.coverageStartDate === 'string' ? data.quote.coverageStartDate : data.quote.coverageStartDate.toISOString();
      const startValidation = validatePentadDate(startDateStr, true);
      setPentadValidation(prev => ({
        ...prev,
        startDateValid: startValidation.isValid,
        startDateMessage: startValidation.message
      }));
    }
    if (data.quote?.coverageEndDate) {
      const endDateStr = typeof data.quote.coverageEndDate === 'string' ? data.quote.coverageEndDate : data.quote.coverageEndDate.toISOString();
      const endValidation = validatePentadDate(endDateStr, false);
      setPentadValidation(prev => ({
        ...prev,
        endDateValid: endValidation.isValid,
        endDateMessage: endValidation.message
      }));
    }
  }, [data.quote?.coverageStartDate, data.quote?.coverageEndDate]);

  // Extract perils and weighting configuration from selected product
  useEffect(() => {
    if (data.quote?.product?.productConfig) {
      try {
        const config = data.quote.product.productConfig as unknown as Record<string, unknown>;
        
        // Handle schema-wrapped format
        const actualConfig = config?.data as Record<string, unknown> || config;
        
        const coverageConfig = actualConfig?.coverageConfig as Record<string, unknown> || {};
        const loadingConfig = actualConfig?.loadingConfig as Record<string, unknown> || {};
        const productPerils = (coverageConfig?.perils as string[]) || [];
        
        // Extract coverage period limits
        const periodLimits = coverageConfig.coveragePeriodLimits as Record<string, unknown>;
        if (periodLimits) {
          setCoveragePeriodLimits({
            type: (periodLimits.type as 'duration_days' | 'date_range') || 'duration_days',
            dateValidation: (periodLimits.dateValidation as 'none' | 'pentads') || 'pentads',
            minDurationDays: periodLimits.minDurationDays as number,
            maxDurationDays: periodLimits.maxDurationDays as number,
            startDateRange: periodLimits.startDateRange as { earliest: string; latest: string },
            endDateRange: periodLimits.endDateRange as { earliest: string; latest: string }
          });
        }
        
        // Map product perils to our peril structure
        const perilOptions = (productPerils as string[]).map((perilName: string) => ({
          id: perilName,
          name: getPerilDisplayName(perilName),
          defaultWeight: Math.round(100 / productPerils.length),
          unit: getPerilUnitLabel(perilName)
        }));
        
        setAvailablePerils(perilOptions);
        
        // Extract weighting enforcement configuration
        const perilWeightingConfig = (loadingConfig.perilWeighting as Record<string, unknown>) || {};
        const enforcement = (perilWeightingConfig.enforcement as string) || 'default';
        setWeightingEnforcement(enforcement as 'default' | 'fixed' | 'range');
        
        const weightsConfig = (perilWeightingConfig.weights as Record<string, unknown>) || {};
        const allPerilIds = perilOptions.map(p => p.id);
        
        // Extract weighting ranges for range enforcement (convert fractions to percentages)
        if (enforcement === 'range' && perilWeightingConfig.weights) {
          const ranges: Record<string, { min: number; max: number }> = {};
          Object.entries(weightsConfig).forEach(([perilId, cfg]) => {
            if (typeof cfg === 'object' && cfg !== null) {
              const obj = cfg as { min: number; max: number };
              const minVal = obj.min <= 1 && obj.min > 0 ? obj.min * 100 : obj.min;
              const maxVal = obj.max <= 1 && obj.max > 0 ? obj.max * 100 : obj.max;
              ranges[perilId] = { min: minVal, max: maxVal };
            }
          });
          setWeightingRanges(ranges);
        }
        
        // Single peril: always 100% weight, auto-select (#74)
        if (allPerilIds.length === 1) {
          const singleWeights = { [allPerilIds[0]]: 100 };
          setSelectedPerils(allPerilIds);
          setPerilWeights(singleWeights);
          const updatedQuote = createCompleteQuote(data, {
            perils_config: {
              perils: [{ peril_id: allPerilIds[0], weight: 100 }],
              total_weight_check: 100
            }
          });
          updateData({ quote: updatedQuote });
          return;
        }
        
        // Fixed enforcement: auto-select all perils with config weights (#200)
        if (enforcement === 'fixed') {
          const rawWeights: Record<string, number> = {};
          allPerilIds.forEach(id => {
            const w = extractWeightFromConfig(weightsConfig[id]);
            rawWeights[id] = w;
          });
          // Normalize to sum to 100 if needed
          const rawTotal = Object.values(rawWeights).reduce((s, v) => s + v, 0);
          const fixedWeights: Record<string, number> = {};
          if (rawTotal > 0) {
            let remaining = 100;
            allPerilIds.forEach((id, i) => {
              if (i === allPerilIds.length - 1) {
                fixedWeights[id] = Math.round(remaining * 10) / 10;
              } else {
                const normalized = Math.round(((rawWeights[id] / rawTotal) * 100) * 10) / 10;
                fixedWeights[id] = normalized;
                remaining -= normalized;
              }
            });
          } else {
            Object.assign(fixedWeights, distributeWeightsEqually(allPerilIds));
          }
          setSelectedPerils(allPerilIds);
          setPerilWeights(fixedWeights);
          const updatedQuote = createCompleteQuote(data, {
            perils_config: {
              perils: allPerilIds.map(id => ({ peril_id: id, weight: fixedWeights[id] })),
              total_weight_check: 100
            }
          });
          updateData({ quote: updatedQuote });
          return;
        }
        
        // Default enforcement: pre-select all perils if none currently selected (#201)
        if (enforcement === 'default') {
          const currentPerils = data.quote?.perils_config?.perils?.map(
            (p: { peril_id: string }) => p.peril_id
          ) || [];
          if (currentPerils.length === 0) {
            const defaultWeights: Record<string, number> = {};
            const hasConfigWeights = allPerilIds.some(id => weightsConfig[id] !== undefined);
            if (hasConfigWeights) {
              allPerilIds.forEach(id => {
                defaultWeights[id] = extractWeightFromConfig(weightsConfig[id]) || perilOptions.find(p => p.id === id)?.defaultWeight || 0;
              });
              const total = Object.values(defaultWeights).reduce((s, v) => s + v, 0);
              if (total > 0 && Math.abs(total - 100) > 0.1) {
                let rem = 100;
                allPerilIds.forEach((id, i) => {
                  if (i === allPerilIds.length - 1) {
                    defaultWeights[id] = Math.round(rem * 10) / 10;
                  } else {
                    const n = Math.round(((defaultWeights[id] / total) * 100) * 10) / 10;
                    defaultWeights[id] = n;
                    rem -= n;
                  }
                });
              }
            } else {
              Object.assign(defaultWeights, distributeWeightsEqually(allPerilIds));
            }
            setSelectedPerils(allPerilIds);
            setPerilWeights(defaultWeights);
            const updatedQuote = createCompleteQuote(data, {
              perils_config: {
                perils: allPerilIds.map(id => ({ peril_id: id, weight: defaultWeights[id] })),
                total_weight_check: Object.values(defaultWeights).reduce((s, v) => s + v, 0)
              }
            });
            updateData({ quote: updatedQuote });
          }
        }
      } catch (error) {
        console.error('Error processing product configuration:', error);
      }
    }
  }, [data.quote?.product]);

  // Helper function to get proper display name for perils
  const getPerilDisplayName = (perilId: string): string => {
    // Find the peril in the configuration by ID
    const peril = perils.find(p => p.id === perilId);
    return peril ? peril.name : perilId;
  };

  // Helper function to get peril unit — delegates to central peril registry
  const getPerilUnitLabel = (perilId: string): string => {
    const registryUnit = getPerilUnitFromRegistry(perilId);
    if (registryUnit) return registryUnit;

    const extraUnits: Record<string, string> = {
      'high-temperature': '°C',
      'low-temperature': '°C',
      'wind': 'km/h',
      'hail': 'occurrence',
    };
    return extraUnits[perilId] || 'units';
  };

  // Validate pentad dates (must start/end on pentad boundaries)
  const validatePentadDate = (dateString: string, isStartDate: boolean) => {
    if (!dateString) return { isValid: true };
    
    // Handle both ISO strings and date-only strings
    const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00Z`);
    const day = date.getUTCDate();
    
    
    if (isStartDate) {
      // Pentads start on 1st, 6th, 11th, 16th, 21st, 26th of month
      const validStartDays = [1, 6, 11, 16, 21, 26];
      const isValid = validStartDays.includes(day);
      
      return {
        isValid,
        message: isValid ? undefined : `Start date must be on pentad boundaries: ${validStartDays.join(', ')}`
      };
    } else {
      // Pentads end on 5th, 10th, 15th, 20th, 25th, or last day of month
      const lastDayOfMonth = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
      const validEndDays = [5, 10, 15, 20, 25, lastDayOfMonth];
      const isValid = validEndDays.includes(day);
      
      return {
        isValid,
        message: isValid ? undefined : `End date must be on pentad boundaries: ${validEndDays.join(', ')} or last day of month (${lastDayOfMonth})`
      };
    }
  };

  // Validate pentad dates (must start/end on pentad boundaries)
  // const validatePentadDates = (startDate: string, endDate: string) => {
  //   const startValidation = validatePentadDate(startDate, true);
  //   const endValidation = validatePentadDate(endDate, false);
  //   
  //   if (!startValidation.isValid && !endValidation.isValid) {
  //     return {
  //       isValid: false,
  //       message: `${startValidation.message}. ${endValidation.message}`
  //     };
  //   } else if (!startValidation.isValid) {
  //     return startValidation;
  //   } else if (!endValidation.isValid) {
  //     return endValidation;
  //   }
  //   
  //   return { isValid: true };
  // };

  // Sum insured handling moved to Step 2 for individual policies

  const handlePerilSelection = (perilId: string, selected: boolean) => {
    // Fixed enforcement: never allow changes
    if (weightingEnforcement === 'fixed') return;

    let newSelectedPerils: string[];
    if (selected) {
      newSelectedPerils = [...selectedPerils, perilId];
    } else {
      newSelectedPerils = selectedPerils.filter(id => id !== perilId);
    }

    // Distribute weights equally among selected perils (#202)
    const newWeights = distributeWeightsEqually(newSelectedPerils);

    // For range enforcement, clamp weights to min/max and redistribute remainder
    if (weightingEnforcement === 'range' && Object.keys(weightingRanges).length > 0) {
      let adjusted = true;
      let iterations = 0;
      while (adjusted && iterations < 10) {
        adjusted = false;
        iterations++;
        let excess = 0;
        const unclamped: string[] = [];
        newSelectedPerils.forEach(id => {
          const range = weightingRanges[id];
          if (range) {
            if (newWeights[id] < range.min) {
              excess += newWeights[id] - range.min;
              newWeights[id] = range.min;
              adjusted = true;
            } else if (newWeights[id] > range.max) {
              excess += newWeights[id] - range.max;
              newWeights[id] = range.max;
              adjusted = true;
            } else {
              unclamped.push(id);
            }
          } else {
            unclamped.push(id);
          }
        });
        if (Math.abs(excess) > 0.05 && unclamped.length > 0) {
          const perItem = excess / unclamped.length;
          unclamped.forEach(id => {
            newWeights[id] = Math.round((newWeights[id] + perItem) * 10) / 10;
          });
        }
      }
    }

    setSelectedPerils(newSelectedPerils);
    setPerilWeights(newWeights);
    
    const updatedQuote = createCompleteQuote(data, {
      perils_config: {
        perils: newSelectedPerils.map(id => ({
          peril_id: id,
          weight: newWeights[id] || 0
        })),
        total_weight_check: Object.values(newWeights).reduce((sum, w) => sum + w, 0)
      }
    });
    
    updateData({ quote: updatedQuote });
  };

  const handleWeightChange = (perilId: string, weight: number) => {
    const newWeights = { ...perilWeights, [perilId]: weight };
    setPerilWeights(newWeights);
    
    // Update quote object with new peril weights
    const updatedQuote = createCompleteQuote(data, {
      perils_config: {
        ...data.quote?.perils_config,
        perils: data.quote?.perils_config?.perils?.map((p: { peril_id: string; weight: number }) => 
          p.peril_id === perilId ? { peril_id: p.peril_id, weight } : { peril_id: p.peril_id, weight: p.weight }
        ) || [],
        total_weight_check: Object.values(newWeights).reduce((sum, w) => sum + w, 0)
      }
    });
    
    updateData({ quote: updatedQuote });
  };

  // const totalWeight = Object.values(perilWeights).reduce((sum, weight) => sum + weight, 0);
  // const isWeightValid = Math.abs(totalWeight - 100) < 0.01;

  const getLocalDateString = (isoString: string) => {
    const d = new Date(isoString);
    const year = d.getUTCFullYear();
    const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = d.getUTCDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if quote is approved (read-only mode)
  const isReadOnly = data.quote?.state === 'approved';

  return (
    <div className={`space-y-6 ${isReadOnly ? 'pointer-events-none opacity-60' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Coverage Period & Perils</h4>
        </div>
        {isReadOnly && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Approved</span>
          </div>
        )}
      </div>

      {/* Coverage Period */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h5 className="font-medium text-gray-900 mb-4">Coverage Period</h5>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date *
            </label>
            <input
              type="date"
              value={data.quote?.coverageStartDate ? getLocalDateString(typeof data.quote.coverageStartDate === 'string' ? data.quote.coverageStartDate : data.quote.coverageStartDate.toISOString()) : ''}
              onChange={(e) => {
                const dateValue = e.target.value ? `${e.target.value}T00:00:00Z` : '';
                
                // Update quote object directly
                const updatedQuote = createCompleteQuote(data, {
                  coverageStartDate: dateValue ? new Date(dateValue) : new Date()
                });
                updateData({ quote: updatedQuote });
                
                // Validate pentad boundaries only if dateValidation is "pentads" (or undefined, which defaults to pentads)
                const dateValidation = coveragePeriodLimits?.dateValidation;
                if (dateValidation !== 'none') {
                  if (dateValue && data.quote?.coverageEndDate) {
                    const startValidation = validatePentadDate(dateValue, true);
                    const endDateStr = typeof data.quote?.coverageEndDate === 'string' ? data.quote.coverageEndDate : data.quote?.coverageEndDate?.toISOString() || '';
                    const endValidation = validatePentadDate(endDateStr, false);
                    
                    setPentadValidation({
                      isValid: startValidation.isValid && endValidation.isValid,
                      startDateValid: startValidation.isValid,
                      endDateValid: endValidation.isValid,
                      startDateMessage: startValidation.message,
                      endDateMessage: endValidation.message
                    });
                  } else if (dateValue) {
                    // Only start date - validate individually
                    const startValidation = validatePentadDate(dateValue, true);
                    setPentadValidation({
                      isValid: startValidation.isValid,
                      startDateValid: startValidation.isValid,
                      startDateMessage: startValidation.message,
                      message: startValidation.message
                    });
                  } else {
                    setPentadValidation({ isValid: true });
                  }
                } else {
                  // No pentad validation needed
                  setPentadValidation({ isValid: true });
                }
              }}
              min={today.toISOString().split('T')[0]}
              max={nextYear.toISOString().split('T')[0]}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                startDateError ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {startDateError && (
              <p className="mt-1 text-sm text-red-600">{startDateError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date *
            </label>
            <input
              type="date"
              value={data.quote?.coverageEndDate ? getLocalDateString(typeof data.quote.coverageEndDate === 'string' ? data.quote.coverageEndDate : data.quote.coverageEndDate.toISOString()) : ''}
              onChange={(e) => {
                const dateValue = e.target.value ? `${e.target.value}T00:00:00Z` : '';
                
                // Update quote object directly
                const updatedQuote = createCompleteQuote(data, {
                  coverageEndDate: dateValue ? new Date(dateValue) : new Date()
                });
                updateData({ quote: updatedQuote });
                
                // Validate pentad boundaries only if dateValidation is "pentads" (or undefined, which defaults to pentads)
                const dateValidation = coveragePeriodLimits?.dateValidation;
                if (dateValidation !== 'none') {
                  if (data.quote?.coverageStartDate && dateValue) {
                    const startDateStr = typeof data.quote?.coverageStartDate === 'string' ? data.quote.coverageStartDate : data.quote?.coverageStartDate?.toISOString() || '';
                    const startValidation = validatePentadDate(startDateStr, true);
                    const endValidation = validatePentadDate(dateValue, false);
                    
                    setPentadValidation({
                      isValid: startValidation.isValid && endValidation.isValid,
                      startDateValid: startValidation.isValid,
                      endDateValid: endValidation.isValid,
                      startDateMessage: startValidation.message,
                      endDateMessage: endValidation.message
                    });
                  } else if (dateValue) {
                    // Only end date - validate individually
                    const endValidation = validatePentadDate(dateValue, false);
                    setPentadValidation({
                      isValid: endValidation.isValid,
                      endDateValid: endValidation.isValid,
                      endDateMessage: endValidation.message,
                      message: endValidation.message
                    });
                  } else {
                    setPentadValidation({ isValid: true });
                  }
                } else {
                  // No pentad validation needed
                  setPentadValidation({ isValid: true });
                }
              }}
              min={data.quote?.coverageStartDate ? getLocalDateString(typeof data.quote.coverageStartDate === 'string' ? data.quote.coverageStartDate : data.quote.coverageStartDate.toISOString()) : today.toISOString().split('T')[0]}
              max={nextYear.toISOString().split('T')[0]}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                endDateError ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {endDateError && (
              <p className="mt-1 text-sm text-red-600">{endDateError}</p>
            )}
          </div>
        </div>
        
        {/* Coverage Period Duration Display */}
        {data.quote?.coverageStartDate && data.quote?.coverageEndDate && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              <strong>Coverage Period:</strong> {Math.ceil((new Date(data.quote.coverageEndDate).getTime() - new Date(data.quote.coverageStartDate).getTime()) / (1000 * 60 * 60 * 24))} days
            </div>
          </div>
        )}
        
        {/* Coverage Period Limits Display */}
        {coveragePeriodLimits && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              <strong>Allowed Range:</strong>
              {coveragePeriodLimits.type === 'duration_days' ? (
                <span className="ml-2">
                  {coveragePeriodLimits.minDurationDays}-{coveragePeriodLimits.maxDurationDays} days
                </span>
              ) : (
                <div className="ml-2 mt-1 space-y-1">
                  <div>Start: {coveragePeriodLimits.startDateRange?.earliest} to {coveragePeriodLimits.startDateRange?.latest}</div>
                  <div>End: {coveragePeriodLimits.endDateRange?.earliest} to {coveragePeriodLimits.endDateRange?.latest}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Coverage Parameters Card - Show for both individual and master policies */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h5 className="font-medium text-gray-900 mb-4">Coverage Parameters</h5>
        
        {!data.quote?.isMasterPolicy ? (
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              <strong>Sum Insured:</strong> ${calculateTotalSumInsured(data).toLocaleString()}
            </div>
            {calculateTotalHectaresAnimalCount(data) > 0 && (
              <div className="text-sm text-gray-600">
                <strong>Hectares/Animals Insured:</strong> {calculateTotalHectaresAnimalCount(data).toLocaleString()}
              </div>
            )}
          </div>
        ) : data.quote?.isMasterPolicy ? (
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              <strong>Sum Insured:</strong> ${calculateTotalSumInsured(data).toLocaleString()}
            </div>
            {calculateTotalHectaresAnimalCount(data) > 0 && (
              <div className="text-sm text-gray-600">
                <strong>Hectares/Animals Insured:</strong> {calculateTotalHectaresAnimalCount(data).toLocaleString()}
              </div>
            )}
            {data.bulkUploadData?.validRecords && (
              <div className="text-sm text-gray-600">
                <strong>Total Farmers:</strong> {data.bulkUploadData.validRecords}
              </div>
            )}
            <div className="text-xs text-gray-500">
              Coverage parameters were specified during bulk farmer upload.
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            Coverage parameters will be set based on policy type selection.
          </div>
        )}
      </div>

      {/* Perils Selection */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h5 className="font-medium text-gray-900 mb-4">Perils Selection & Weighting</h5>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select the weather perils to be covered and set their relative weights (must sum to 100%).
          </p>

          {weightingEnforcement === 'fixed' && (
            <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-sm text-blue-700">
              Peril weights are fixed for this product.
            </div>
          )}

          {availablePerils.length === 1 && (
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-600">
              This product has a single peril — weight is automatically set to 100%.
            </div>
          )}

          {availablePerils.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              {data.quote?.product ? 'No perils configured for this product' : 'Please select a product first'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Selected</TableHead>
                  <TableHead>Peril Name</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Weight (%)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {availablePerils.map((peril) => {
                  const isSelected = selectedPerils.includes(peril.id);
                  const weight = perilWeights[peril.id] || 0;
                  
                  return (
                    <TableRow key={peril.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handlePerilSelection(peril.id, e.target.checked)}
                          disabled={weightingEnforcement === 'fixed' || availablePerils.length === 1}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {peril.name}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">{peril.unit}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {isSelected ? (
                          <div className="flex items-center justify-end gap-2">
                            <input
                              type="number"
                              value={weight}
                              onChange={(e) => {
                                const newWeight = parseFloat(e.target.value);
                                if (!isNaN(newWeight) && newWeight >= 0 && newWeight <= 100) {
                                  handleWeightChange(peril.id, newWeight);
                                }
                              }}
                              min={weightingEnforcement === 'range' ? (weightingRanges[peril.id]?.min || 0) : 0}
                              max={weightingEnforcement === 'range' ? (weightingRanges[peril.id]?.max || 100) : 100}
                              step={0.1}
                              readOnly={weightingEnforcement === 'fixed' || availablePerils.length === 1}
                              disabled={weightingEnforcement === 'fixed' || availablePerils.length === 1}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                            />
                            {weightingEnforcement === 'fixed' && (
                              <span className="text-xs text-blue-600">(Fixed)</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Total Weight Row */}
                <TableRow className="border-t-2 border-gray-300 bg-gray-50">
                  <TableCell></TableCell>
                  <TableCell className="font-semibold">Total</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right font-semibold">
                    {Object.values(perilWeights).reduce((sum, weight) => sum + weight, 0).toFixed(1)}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}

        </div>
      </div>



      {/* Note: Removed custom error display - validation now handled by standard wizard error card */}
    </div>
  );
}
